import { fetchPrivateApi } from '@/lib/api/apiServiceBase.ts';
import { TableDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { BookingListDto } from '@/features/dashboard/bookings/types/BookingListDto.ts';
import { BookingSnapshotDto } from '@/features/dashboard/bookings/types/BookingSnapshotDto.ts';
import { BookingDetailsDto } from '@/features/dashboard/bookings/types/BookingDetailsDto.ts';
import { BookingBillDto } from '@/features/dashboard/bookings/types/BookingBillDto.ts';
import { BookingTransactionListDto } from '@/features/dashboard/bookings/types/BookingTransactionListDto.ts';
import { ParticipantSelectionsDto } from '@/features/dashboard/bookings/types/ParticipantSelectionsDto.ts';
import { CreateBookingRefundRequestDto } from '@/features/dashboard/bookings/types/CreateBookingRefundRequestDto.ts';
import { FinancialTransactionDto } from '@/features/dashboard/financialTransactions/types/FinancialTransactionDto.ts';
import { buildPageRequestParams } from '@/components/table/types/TableUtil.ts';

/** Górna granica `PageRequestDto.size` po stronie backendu. */
const BOOKING_TRANSACTIONS_PAGE_SIZE = 200;

const EMPTY_FILTER_CRITERIA: FilterCriteriaDto = {
  filters: [],
  combinator: 'AND',
  searchTerm: '',
  quickFilters: {},
};

const bookingsApi = {
  getTableDefinition: async (): Promise<TableDefinitionDto> => {
    return await fetchPrivateApi<TableDefinitionDto>(
      'GET',
      'bookings/table-definition',
    );
  },

  getAll: async (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ): Promise<PagedDto<BookingListDto>> => {
    const params = buildPageRequestParams(pageRequest);
    return await fetchPrivateApi<PagedDto<BookingListDto>>(
      'POST',
      `bookings/all?${params.toString()}`,
      filterCriteria,
    );
  },

  getDetails: async (bookingId: string): Promise<BookingDetailsDto> => {
    return await fetchPrivateApi<BookingDetailsDto>(
      'GET',
      `bookings/${bookingId}`,
    );
  },

  getBill: async (bookingId: string): Promise<BookingBillDto> => {
    return await fetchPrivateApi<BookingBillDto>(
      'GET',
      `bookings/${bookingId}/bill`,
    );
  },

  /**
   * Wpłaty rezerwacji. Endpoint jest tabelowy, więc bierzemy jedną stronę
   * maksymalnego rozmiaru (`@Max(200)` na `PageRequestDto.size`) z pustymi
   * kryteriami — rachunek pokazuje komplet, a nie stronicuje. Rezerwacja
   * z ponad dwustoma wpłatami pokaże dwieście najstarszych; zwykłego odczytu
   * listy wpłat per rezerwacja backend nie ma (MODEL_ALIGNMENT.md).
   */
  getTransactions: async (
    bookingId: string,
  ): Promise<PagedDto<BookingTransactionListDto>> => {
    const params = buildPageRequestParams({
      page: 0,
      size: BOOKING_TRANSACTIONS_PAGE_SIZE,
    });
    return await fetchPrivateApi<PagedDto<BookingTransactionListDto>>(
      'POST',
      `bookings/${bookingId}/transactions/all?${params.toString()}`,
      EMPTY_FILTER_CRITERIA,
    );
  },

  /**
   * Wybory kompletu uczestników. Osobno od `GET bookings/{id}`, bo nagłówek
   * rezerwacji czytają też ekrany, których wybory nie obchodzą, i bo wybory
   * unieważnia co innego niż dane zapisującego, saldo i raty.
   */
  getParticipantSelections: async (
    bookingId: string,
  ): Promise<ParticipantSelectionsDto[]> => {
    return await fetchPrivateApi<ParticipantSelectionsDto[]>(
      'GET',
      `bookings/${bookingId}/participant-selections`,
    );
  },

  getSnapshot: async (bookingId: string): Promise<BookingSnapshotDto> => {
    return await fetchPrivateApi<BookingSnapshotDto>(
      'GET',
      `bookings/${bookingId}/snapshot`,
    );
  },

  createRefund: async (
    bookingId: string,
    request: CreateBookingRefundRequestDto,
  ): Promise<FinancialTransactionDto> => {
    return await fetchPrivateApi<FinancialTransactionDto>(
      'POST',
      `bookings/${bookingId}/refunds`,
      request,
    );
  },
};

export default bookingsApi;
